import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { ResponsiveTreeMap } from '@nivo/treemap'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { useFirmReportData } from '../../../../../api/coreData'
import { useDenaliFilter } from '../../filters/DenaliFilterContextProvider'

const useFormatters = (valueFormat) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => {
    return formatter(d, valueFormat)
  }, [formatter, valueFormat])

  return {
    valueFormatter
  }
}

function groupData (data, indexBy, valueAccessor) {
  // Recursive function: at each level, group the items by the first property in indexBy
  const [currentKey, ...restKeys] = indexBy

  const groups = data.reduce((acc, item) => {
    const key = item[currentKey]
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})

  return Object.keys(groups).map(key => {
    const items = groups[key]
    if (restKeys.length === 0) {
      // Leaf node: use the first item in the group for the aggregated value
      return { name: key, value: items[0][valueAccessor] }
    }
    // Recursive grouping for children
    return { name: key, children: groupData(items, restKeys, valueAccessor) }
  })
}

function DenaliTreeMap ({
  reportId,
  baseQuery,
  indexBy,
  valueAccessor,
  valueFormat,
  height,
  rootLabel,
  ...chartProps
}) {
  const { filter, membershipFilter } = useDenaliFilter()
  const query = useMemo(() => {
    return {
      ...baseQuery,
      membershipFilter,
      filters: {
        ...(filter || {}),
        ...(baseQuery?.filters || {})
      },
      reportId
    }
  }, [reportId, baseQuery, filter, membershipFilter])

  const { data: report, isLoading } = useFirmReportData(query)
  const chartData = useMemo(() => {
    if (!report) return {}

    const root = {
      name: rootLabel,
      children: groupData(report.data, indexBy, valueAccessor)
    }

    return root
  }, [report, indexBy, valueAccessor, rootLabel])
  const { valueFormatter } = useFormatters(valueFormat)

  if (isLoading) return null

  return (
    <div style={{ height }}>
      <ResponsiveTreeMap
        theme={{
          text: {
            fontFamily: 'var(--font-sans) !important',
            fontSize: '12px',
            color: 'var(--text-color-main)'
          }
        }}
        data={chartData}
        valueFormat={valueFormatter}
        identity='name'
        value='value'
        {...chartProps}
      />
    </div>
  )
}

DenaliTreeMap.propTypes = {
  reportId: PropTypes.string,
  baseQuery: PropTypes.object,
  indexBy: PropTypes.string,
  rootLabel: PropTypes.string,
  valueAccessor: PropTypes.string,
  valueFormat: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}

DenaliTreeMap.defaultProps = {
  valueFormat: 'marketValue',
  height: 300,
  rootLabel: 'All'
}

export default DenaliTreeMap
